import { Form, Input, Select, Space } from "antd"
import { NewCustomerData, createEmptyCustomerData, newCustomerValidationRules } from "./newJobModel"
import { getAll as getAllCountries } from "../../../services/countriesService"
import { getForCountry } from "../../../services/statesService"
import { useState } from "react"
import WrappedGooglePlacesAutoCompleteInput from "../../common/GooglePlacesAutoCompleteInput"
import { PlaceData } from "../../common/googlePlacesAutoCompleteModel"

type Props = {
  googleMapsApiKey: string
  onChange: (data: NewCustomerData) => void
}

const defaultInputStyle = { width: '390px' }

const NewCustomerForm = ({googleMapsApiKey, onChange}: Props) => {
  const initialValues = createEmptyCustomerData()
  const [stateOptions, setStateOptions] = useState(buildStateOptions(initialValues.countryCode))
  const [stateLabel, setStateLabel] = useState(getStateLabel(initialValues.countryCode))
  const [form] = Form.useForm<NewCustomerData>()

  const handlePlaceData = (data: PlaceData) => {
    const stateOptions = buildStateOptions(data.countryCode)
    setStateOptions(stateOptions)
    setStateLabel(getStateLabel(data.countryCode))

    form.setFieldsValue({
      address1: data.address1,
      city: data.city,
      zip: data.zip,
      countryCode: data.countryCode,
      state: stateOptions.length > 0 ? data.stateShort : data.stateLong,
      latitude: data.location?.latitude,
      longitude: data.location?.longitude
    })

    onChange(form.getFieldsValue())
  }

  return <Form layout="vertical" initialValues={initialValues} form={form}
    onValuesChange={(x, all) => {
      if (x.countryCode) {
        setStateOptions(buildStateOptions(x.countryCode))
        setStateLabel(getStateLabel(x.countryCode))

        if (all.state) {
          form.setFieldValue('state', undefined)
          form.validateFields(['state'])
        }
      }
    }}
    onFieldsChange={() => onChange(form.getFieldsValue())}
  >
    <Form.Item name="latitude" hidden />
    <Form.Item name="longitude" hidden />

    <Space size="large" align="start">
      <Form.Item label="Company" name="company"  style={defaultInputStyle} rules={formRules.company}>
        <Input />
      </Form.Item>
      <Form.Item label="First Name" name="firstName"  style={{width: '190px'}} rules={formRules.firstName}>
        <Input />
      </Form.Item>
      <Form.Item label="Last Name" name="lastName"  style={{width: '190px'}} rules={formRules.lastName}>
        <Input  />
      </Form.Item>
    </Space>

    <Form.Item label="Property Nickname" name="nickname" style={defaultInputStyle} rules={formRules.nickname}>
      <Input />
    </Form.Item>

    <Space size="large" align="start">
      <Form.Item label="Address" name="address1" style={defaultInputStyle} rules={formRules.address1}>
        <WrappedGooglePlacesAutoCompleteInput onPlaceData={handlePlaceData} apiKey={googleMapsApiKey} />
      </Form.Item>
      <Form.Item label="Address (continued)" name="address2" style={defaultInputStyle} rules={formRules.address2}>
        <Input />
      </Form.Item>
    </Space>

    <Space size="large" align="start">
      <Form.Item label="City" name="city" style={defaultInputStyle} rules={formRules.city}>
        <Input />
      </Form.Item>
      <Form.Item label="Zip" name="zip" style={defaultInputStyle} rules={formRules.zip}>
        <Input />
      </Form.Item>
    </Space>

    <Space size="large" align="start">
      <Form.Item label="Country" name="countryCode" style={defaultInputStyle} rules={formRules.countryCode}>
        <Select options={countryOptions} />
      </Form.Item>
      <Form.Item label={stateLabel} name="state" style={defaultInputStyle} rules={formRules.state}>
        { stateOptions.length > 0 ? <Select options={stateOptions} /> : <Input /> }
      </Form.Item>
    </Space>

    <Space size="large" align="start">
      <Form.Item label="Phone" name="phoneType" style={{width: '120px'}}>
        <Select options={phoneOptions} />
      </Form.Item>
      <Form.Item label=" " name="phoneNumber" style={{width: '245px'}} rules={formRules.phoneNumber}>
        <Input placeholder="primary phone number" />
      </Form.Item>
      <Form.Item label="Email" name="email" style={defaultInputStyle} rules={formRules.email}>
        <Input placeholder="primary email address" />
      </Form.Item>
    </Space>
  </Form>
}

export default NewCustomerForm

const phoneOptions = [
  { label: 'Company', value: 0 },
  { label: 'Home', value: 1 },
  { label: 'Mobile', value: 2 },
  { label: '(Other)', value: 3 },
]

const displayNames: any = {
  company: 'Company',
  firstName: 'First Name',
  lastName: 'Last Name',
  nickname: 'Property Nickname',
  address1: 'Address',
  address2: 'Address',
  city: 'City',
  zip: 'Zip',
  countryCode: 'Country',
  state: 'State',
  phoneType: 'Phone type',
  phoneNumber: 'Phone',
  email: 'Email'
}

const formRules = Object.entries(newCustomerValidationRules).reduce((acc:any, [key, value]) => {
  const rules = []
  if (value.required) {
    rules.push({ required: true, message: `${displayNames[key]} is required` })
  }
  if (value.maxLength) {
    rules.push({ type: 'string', max: value.maxLength, message: `${displayNames[key]} may not be longer than ${value.maxLength} characters`})
  }
  acc[key] = rules
  return acc
}, {})

const countryOptions = getAllCountries().map((country) => ({
  label: country.name,
  value: country.code
}))

const buildStateOptions = (countryCode: string) => {
  const states = getForCountry(countryCode) ?? []
  return states.map((state) => ({
    label: state.name,
    value: state.code
  }))
}

const getStateLabel = (countryCode: string) => {
  return getAllCountries().find(c => c.code === countryCode)?.stateLabel ?? 'State / Province / Region'
}